// What the user should be told when a login step fails.
//
// Before this split every non-MFA failure rendered "Feil brukernavn eller
// passord" — including the browser never reaching the API host (DNS
// cutover, wrong base URL, LB error page) — which sends support down the
// wrong path. Pure module: no imports, so it is unit-testable and safe to use
// from the reducer/components as well as the thunks.

export const LOGIN_FAILURE_KIND = {
  CREDENTIALS: 'credentials',
  UNAVAILABLE: 'unavailable',
};

// Firebase SDK errors that mean "could not talk to the service", not "the
// service said no". Everything else under auth/* (wrong-password,
// user-not-found, invalid-credential, too-many-requests, blocking-function
// rejections, …) is a decision the service made and stays a credential error.
const FIREBASE_TRANSPORT_CODES = [
  'auth/network-request-failed',
  'auth/timeout',
  // Firestore (direct reads during MFA finalize) reports transport trouble
  // with a bare gRPC-style code.
  'unavailable',
];

// HTTP statuses from OUR API (customFetch → ApiError) that mean the request
// never reached a working api-internal: an unknown host serving a 404/410, or
// the service failing. 4xx decisions (400/401/403/422/429) stay credential
// errors — the backend answers those deliberately.
const UNAVAILABLE_API_STATUSES = [404, 410];

const isFirebaseError = (error) => typeof error?.code === 'string';

// customFetch wraps everything in ApiError, whose `status` is the HTTP
// status, or undefined when fetch itself rejected (DNS/TLS/refused/CORS —
// the browser gives a TypeError with no response).
const isApiErrorShape = (error) => error != null && typeof error === 'object' && 'status' in error;

export const classifyLoginFailure = (error) => {
  if (isFirebaseError(error)) {
    return FIREBASE_TRANSPORT_CODES.includes(error.code)
      ? LOGIN_FAILURE_KIND.UNAVAILABLE
      : LOGIN_FAILURE_KIND.CREDENTIALS;
  }

  if (isApiErrorShape(error)) {
    const status = Number(error.status);
    const noResponse = !Number.isFinite(status) || status === 0;
    // api-internal always answers JSON. A 4xx with no parsed body (an HTML
    // 401/403 from a proxy, LB or wrong host — customFetch leaves `body`
    // null then) never came from a working API either.
    const nonJsonBody = error.body == null;
    if (noResponse || nonJsonBody || status >= 500 || UNAVAILABLE_API_STATUSES.includes(status)) {
      return LOGIN_FAILURE_KIND.UNAVAILABLE;
    }
    return LOGIN_FAILURE_KIND.CREDENTIALS;
  }

  // A raw fetch rejection that did not go through customFetch.
  if (error instanceof TypeError) {
    return LOGIN_FAILURE_KIND.UNAVAILABLE;
  }

  return LOGIN_FAILURE_KIND.CREDENTIALS;
};

export const isLoginUnavailable = (error) =>
  classifyLoginFailure(error) === LOGIN_FAILURE_KIND.UNAVAILABLE;
